import { z } from "zod";

import type { IdentityApi, Profile, SecondFactor, SecurityStatus, TotpEnrolment } from "../types";
import type { LiveCtx } from "./auth";
import { ROUTES } from "./routes";
import { maybe, parser, type Parser } from "./schemas";

type AccountApi = Pick<
  IdentityApi,
  "getProfile" | "updateProfile" | "getSecurity" | "enrolTotp" | "verifyTotp" | "removeSecondFactor"
>;

const profileSchema = z.object({
  name: z.string(),
  email: z.string(),
  timeZone: z.string(),
  units: z.enum(["metric", "imperial"]),
  coaRegistration: maybe(z.object({ number: z.string(), validUntil: maybe(z.string()) })),
});

/** What the Two-step verification card shows for each kind of factor. */
const METHOD_LABELS: Readonly<Record<SecondFactor["kind"], string>> = {
  totp: "Authenticator app",
  sms: "Text message",
  other: "One-time code",
};

const factorSchema = z.object({ id: z.string(), kind: z.string(), createdAt: z.string() });

const securitySchema = z
  .object({
    factors: z.array(factorSchema),
    requiredByOrganisation: z.boolean(),
    signInMethod: z.enum(["email_code", "passkey", "google", "microsoft"]),
  })
  .transform((reply): SecurityStatus => {
    const factors: SecondFactor[] = reply.factors.map((factor) => ({
      id: factor.id,
      kind: factor.kind === "totp" || factor.kind === "sms" ? factor.kind : "other",
      createdAt: factor.createdAt,
    }));
    const first = factors[0];
    return {
      twoStep: {
        enabled: first !== undefined,
        method: first === undefined ? undefined : METHOD_LABELS[first.kind],
      },
      factors,
      requiredByOrganisation: reply.requiredByOrganisation,
      signInMethod: reply.signInMethod,
    };
  });

const enrolmentSchema = z.object({
  factorId: z.string(),
  challengeId: z.string(),
  qrCode: z.string().startsWith("data:image/"),
  secret: z.string(),
});

export const parseProfile: Parser<Profile> = parser(profileSchema);
export const parseSecurity: Parser<SecurityStatus> = parser(securitySchema);
export const parseEnrolment: Parser<TotpEnrolment> = parser(enrolmentSchema);

/**
 * The signed-in person's own account (FR-ADMIN-007, 044; FR-PROJ-121) on the identity service: the profile (name at
 * WorkOS, preferences in Orthos) and two-step verification through the WorkOS MFA API. Enrolling and removing a
 * factor need a recent sign-in; the service answers `step_up_required` otherwise.
 */
export function createAccountApi(ctx: LiveCtx): AccountApi {
  const { http, signals } = ctx;
  return {
    getProfile: () => http.get(ROUTES.profile, parseProfile),
    async updateProfile(patch) {
      const body = {
        ...patch,
        ...(patch.coaRegistration === undefined
          ? {}
          : {
              coaRegistration: {
                number: patch.coaRegistration.number,
                validUntil: patch.coaRegistration.validUntil ?? null,
              },
            }),
      };
      const profile = await http.send("PATCH", ROUTES.profile, body, parseProfile);
      // Other tabs show the name in their menus too.
      signals.announce();
      return profile;
    },
    getSecurity: () => http.get(ROUTES.security, parseSecurity),
    enrolTotp: () => http.send("POST", ROUTES.totp, {}, parseEnrolment),
    verifyTotp: ({ factorId, challengeId }, code) =>
      http.send("POST", ROUTES.verifyTotp, { factorId, challengeId, code }, parseSecurity),
    removeSecondFactor: (factorId) =>
      http.send("DELETE", ROUTES.factor(factorId), undefined, parseSecurity),
  };
}
